import type {GraphAtom} from "../graph/GraphAtom"
import type {GraphBond} from "../graph/GraphBond"
import {MolecularGraph} from "../graph/MolecularGraph"
import {extractorUtil} from "./extractorUtil";

const shortestPathFinder = {
    bfs: (graph: number[][], S: number, par: number[], dist: number[]) => {
        const q: number[] = [];
        dist[S] = 0;
        q.push(S);

        while (q.length > 0) {
            const node = q.shift()!;

            for (const neighbor of graph[node]) {
                if (dist[neighbor] === Infinity) {
                    par[neighbor] = node;
                    dist[neighbor] = dist[node] + 1;
                    q.push(neighbor);
                }
            }
        }
    },
    getShortestPath: (graph: number[][], S: number, D: number, V: number)=> {
        const par = Array(V).fill(-1);
        const dist = Array(V).fill(Infinity);
        shortestPathFinder.bfs(graph, S, par, dist);
        const path: number[] = [];

        if (dist[D] === Infinity) {
            return path;
        }

        let currentNode = D;
        path.push(D);
        while (par[currentNode] !== -1) {
            path.push(par[currentNode]);
            currentNode = par[currentNode];
        }

        return path.reverse();
    }
}

export type chainFinderOptions = {
    includeSubChains? : boolean,
    onlyCarbonChains? : boolean
}

export const chainFinder = {
    findChains: (graph: MolecularGraph, options?: chainFinderOptions) => {
        let bondMap:  Map<GraphAtom, GraphBond[]>;
        if(options?.onlyCarbonChains) bondMap = extractorUtil.findCarbonChainNetworks(graph);
        else bondMap = extractorUtil.findAtomChainNetworks(graph);

        const atoms = Array.from(bondMap.keys());
        const bonds = Array.from(new Set(Array.from(bondMap.values()).flat()));

        const adjGraph: number[][] = Array.from({length: atoms.length}, () => []);
        for (const bond of bonds) {
            const a1 = atoms.indexOf(bond.a1);
            const a2 = atoms.indexOf(bond.a2);
            if(a1 !== -1 && a2 !== -1) {
                adjGraph[a1].push(a2);
                adjGraph[a2].push(a1);
            }
        }

        const terminalAtomsIndexes = atoms.filter(a => bondMap.get(a)?.length === 1).map(a => atoms.indexOf(a));
        const paths : number[][] = []

        for (let i = 0; i < terminalAtomsIndexes.length - 1; i++) {
            for (let j = i + 1; j < terminalAtomsIndexes.length; j++) {
                const idx1 = terminalAtomsIndexes[i];
                const idx2 = terminalAtomsIndexes[j];
                const path = shortestPathFinder.getShortestPath(adjGraph, idx1, idx2, atoms.length);
                if (path.length > 0) {
                    if(options?.includeSubChains) {
                        // If you're looking at sub chains, then you have to do them in
                        // reverse too. Otherwise the trimmed tails don't get considered
                        // as path starts.
                        // e.g.
                        // in path 5-4->3->2->1
                        // subpath 1->2->3 is valid, but wouldn't be found
                        [path.slice(), path.slice().reverse()].forEach(p => {
                            while(p.length > 0) {
                                paths.push(p.slice());
                                p.pop();
                            }})
                    } else {
                        paths.push(path);
                    }
                }
            }
        }

        // Push lone atoms onto the list. If they were candidates for chains, that means that they
        // got filtered out by rings or other chains. So they are still methyls.
        const loneAtomsIndexes = atoms.filter(a => bondMap.get(a)?.length === 0 && graph.getBondsAttachedTo(a).length > 0).map(a => atoms.indexOf(a));
        loneAtomsIndexes.forEach(i => paths.push([i]));

        return paths.map(path => path.map(idx => atoms[idx]));
    }
}
